import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, MapPin, Search } from 'lucide-react';
import { Button } from './ui/button';

/** List-style Colleges & Commissions view — scales to 100s of colleges.
 *  Search matches college name, city, province, contact AND program names;
 *  when a program matches, the college row auto-expands and highlights it. */
export default function CollegeListView({ colleges, onEdit, onDelete, onSetCommission, onOpenRates, readOnly = false, showCommissions = true }) {
  const [query, setQuery] = useState('');
  const [province, setProvince] = useState('all');
  const [city, setCity] = useState('all');
  const [currency, setCurrency] = useState('all');
  const [maxTuition, setMaxTuition] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [expanded, setExpanded] = useState(new Set());

  const provinces = useMemo(
    () => [...new Set(colleges.map((c) => (c.province || '').trim()).filter(Boolean))].sort(),
    [colleges]
  );
  const cities = useMemo(
    () => [...new Set(colleges.map((c) => (c.city || '').trim()).filter(Boolean))].sort(),
    [colleges]
  );
  const currencies = useMemo(
    () => [...new Set(colleges.flatMap((c) => (c.programs || []).map((p) => (p.currency || '').trim().toUpperCase())).filter(Boolean))].sort(),
    [colleges]
  );

  const filtersActive = query || province !== 'all' || city !== 'all' || currency !== 'all' || maxTuition;
  const clearFilters = () => { setQuery(''); setProvince('all'); setCity('all'); setCurrency('all'); setMaxTuition(''); setSortBy('name'); };

  const q = query.trim().toLowerCase();
  const rows = useMemo(() => {
    const max = maxTuition ? Number(maxTuition) : null;
    const list = colleges
      .map((c) => {
        const programMatches = q
          ? (c.programs || []).filter((p) => (p.program_name || '').toLowerCase().includes(q))
          : [];
        const collegeMatch =
          !q ||
          (c.name || '').toLowerCase().includes(q) ||
          (c.city || '').toLowerCase().includes(q) ||
          (c.province || '').toLowerCase().includes(q) ||
          (c.authorized_contact_email || '').toLowerCase().includes(q) ||
          (c.authorized_contact_name || '').toLowerCase().includes(q);
        return { college: c, programMatches, visible: collegeMatch || programMatches.length > 0 };
      })
      .filter((r) => r.visible)
      .filter((r) => province === 'all' || (r.college.province || '').trim() === province)
      .filter((r) => city === 'all' || (r.college.city || '').trim() === city)
      .filter((r) =>
        currency === 'all' ||
        (r.college.programs || []).some((p) => (p.currency || '').trim().toUpperCase() === currency)
      )
      .filter((r) =>
        max === null || Number.isNaN(max) ||
        (r.college.programs || []).some((p) => Number(p.tuition_fee || 0) <= max)
      );

    const minFee = (c) => Math.min(...((c.programs || []).map((p) => Number(p.tuition_fee || 0)).filter((n) => n > 0)), Infinity);
    const sorters = {
      name: (a, b) => (a.college.name || '').localeCompare(b.college.name || ''),
      programs: (a, b) => ((b.college.programs || []).length - (a.college.programs || []).length),
      tuition_low: (a, b) => minFee(a.college) - minFee(b.college),
      tuition_high: (a, b) => minFee(b.college) - minFee(a.college),
    };
    return list.sort(sorters[sortBy] || sorters.name);
  }, [colleges, q, province, city, currency, maxTuition, sortBy]);

  const isOpen = (c, programMatches) =>
    expanded.has(c.id) || (q && programMatches.length > 0);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const fmtShare = (p, kind) => {
    const cur = p.currency || 'INR';
    const rate = p[`${kind}_rate`];
    const pct = p[`${kind}_percent`];
    if (rate) return `${cur} ${Number(rate).toLocaleString()} flat`;
    if (pct) return `${pct}% of tuition`;
    return '—';
  };

  return (
    <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden" data-testid="college-list-view">
      {/* Filters bar */}
      <div className="px-5 py-4 border-b border-tge-navy/5 space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-tge-navy/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by college name, program name, city or contact…"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-tge-navy/15 bg-white text-sm focus:outline-none focus:border-tge-navy/40"
              data-testid="college-search"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-10 rounded-lg border border-tge-navy/15 bg-white px-3 text-sm"
            data-testid="college-sort"
          >
            <option value="name">Sort: Name A–Z</option>
            <option value="programs">Sort: Most programs</option>
            <option value="tuition_low">Sort: Lowest tuition</option>
            <option value="tuition_high">Sort: Highest tuition</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            className="h-9 rounded-lg border border-tge-navy/15 bg-white px-3 text-xs"
            data-testid="college-province-filter"
          >
            <option value="all">{provinces.length ? 'Province: All' : readOnly ? 'Province: no data yet' : 'Province: no data yet — add it via Edit'}</option>
            {provinces.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="h-9 rounded-lg border border-tge-navy/15 bg-white px-3 text-xs"
            data-testid="college-city-filter"
          >
            <option value="all">{cities.length ? 'City: All' : readOnly ? 'City: no data yet' : 'City: no data yet — add it via Edit'}</option>
            {cities.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="h-9 rounded-lg border border-tge-navy/15 bg-white px-3 text-xs"
            data-testid="college-currency-filter"
          >
            <option value="all">Currency: All</option>
            {currencies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="number"
            min="0"
            value={maxTuition}
            onChange={(e) => setMaxTuition(e.target.value)}
            placeholder="Max tuition"
            className="h-9 w-28 rounded-lg border border-tge-navy/15 bg-white px-3 text-xs focus:outline-none focus:border-tge-navy/40"
            data-testid="college-max-tuition-filter"
          />
          {filtersActive && (
            <button
              onClick={clearFilters}
              className="h-9 px-3 rounded-lg text-xs font-semibold text-rose-600 border border-rose-200 bg-rose-50 hover:bg-rose-100 transition"
              data-testid="college-clear-filters"
            >
              ✕ Clear filters
            </button>
          )}
          <div className="ml-auto flex items-center text-xs text-tge-muted px-1 shrink-0" data-testid="college-result-count">
            {rows.length} of {colleges.length} colleges
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-10 text-sm text-tge-muted">No colleges match your search.</div>
      ) : (
        <div className="divide-y divide-tge-cream">
          {rows.map(({ college: c, programMatches }) => {
            const open = isOpen(c, programMatches);
            const shownPrograms = q && programMatches.length > 0 ? programMatches : c.programs || [];
            return (
              <div key={c.id} data-testid={`college-row-${c.id}`}>
                <div className="px-5 py-3.5 flex items-center gap-3 hover:bg-tge-cream-soft/40 transition">
                  <button
                    onClick={() => toggle(c.id)}
                    className="w-7 h-7 rounded-lg hover:bg-tge-cream flex items-center justify-center text-tge-navy/60 shrink-0"
                    data-testid={`college-expand-${c.id}`}
                  >
                    {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </button>
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => toggle(c.id)}>
                    <div className="font-medium text-tge-navy text-sm truncate">{c.name}</div>
                    <div className="text-[11px] text-tge-muted flex items-center gap-2 flex-wrap">
                      {(c.city || c.province) && (
                        <span className="inline-flex items-center gap-1"><MapPin size={10} />{[c.city, c.province].filter(Boolean).join(', ')}</span>
                      )}
                      <span>{c.authorized_contact_email}</span>
                      <span className="font-semibold text-tge-navy/60">{(c.programs || []).length} program{(c.programs || []).length === 1 ? '' : 's'}</span>
                      {q && programMatches.length > 0 && (
                        <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-semibold">
                          {programMatches.length} matching program{programMatches.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!readOnly && (
                      <>
                        <Button size="sm" variant="outline" className="text-[11px] h-7" onClick={() => onSetCommission(c)} data-testid={`college-set-commission-${c.id}`}>
                          Set Commission
                        </Button>
                        {(c.programs || []).length > 0 && (
                          <Button size="sm" variant="outline" className="text-[11px] h-7" onClick={() => onOpenRates(c)} data-testid={`college-rates-btn-${c.id}`}>
                            💰 Rates
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="text-[11px] h-7" onClick={() => onEdit(c)} data-testid={`college-edit-${c.id}`}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-[11px] h-7 text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200" onClick={() => onDelete(c)} data-testid={`college-delete-${c.id}`}>
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {open && (
                  <div className="px-14 pb-4" data-testid={`college-programs-${c.id}`}>
                    {shownPrograms.length === 0 ? (
                      <div className="text-xs text-tge-muted italic">No programs added yet.</div>
                    ) : (
                      <table className="w-full text-xs border border-tge-navy/10 rounded-lg overflow-hidden">
                        <thead className="bg-tge-cream-soft text-tge-muted">
                          <tr>
                            {['Program', 'Duration', 'Tuition fee', ...(showCommissions ? ['College → TGE', 'TGE → Recruiter'] : [])].map((h) => (
                              <th key={h} className="text-left px-3 py-2 font-semibold uppercase tracking-wide text-[10px]">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-tge-cream bg-white">
                          {shownPrograms.map((p, i) => {
                            const highlight = q && (p.program_name || '').toLowerCase().includes(q);
                            return (
                              <tr key={i} className={highlight ? 'bg-emerald-50/60' : ''}>
                                <td className="px-3 py-2 font-medium text-tge-navy">{p.program_name}</td>
                                <td className="px-3 py-2 text-tge-muted">{p.duration || '—'}</td>
                                <td className="px-3 py-2">{(p.currency || 'INR')} {Number(p.tuition_fee || 0).toLocaleString()}</td>
                                {showCommissions && (
                                  <>
                                    <td className="px-3 py-2 text-emerald-700">{fmtShare(p, 'college_to_tge')}</td>
                                    <td className="px-3 py-2 text-amber-700">{fmtShare(p, 'tge_to_recruiter')}</td>
                                  </>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                    {q && programMatches.length > 0 && programMatches.length < (c.programs || []).length && (
                      <button onClick={() => toggle(c.id)} className="text-[11px] text-tge-navy/50 hover:text-tge-navy mt-1.5 underline">
                        Showing matching programs only — click to toggle all {(c.programs || []).length}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
